import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, from, of, throwError, firstValueFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { ApiResponse, ApiPaginatedResponse, ApiErrorCode } from './api.interface';
import {
  PullTestingModule,
  PullTestPiling,
  PullTestingModuleStatus,
  emptyPullTestingModuleStatus,
  BulkCreatePullTestPilingsRequest,
  PullTestPilingUpdate,
  PileLoadTestLogReportRequest,
  PileSummaryReportRequest,
  PullTestDeflectionGaugeMisalignmentActionPayload,
  PullTestingProjectActionPayload,
  PULL_TESTING_PROJECT_ACTION_MODULE_TYPE,
  PullTestEventsLogEntry,
  PullTestingEventsLogQuery,
  ResolveCollisionRequest,
  ResolveCollisionResponseData,
} from 'shared_models';
import {
  buildPullTestLocationKey,
  collectCompletedPileIdentifiers,
} from '../modules/pull-testing/pull-test-location.util';

const PILE_IDENTIFIERS_BY_LOCATION_PAGE_SIZE = 100;
const PILE_IDENTIFIERS_BY_LOCATION_MAX_PAGES = 20;

/**
 * Pull Testing module and PullTestPiling records (`/projects/{projectID}/pull-testing/...`).
 */
@Injectable({
  providedIn: 'root',
})
export class PullTestingService {
  private apiUrl = environment.apiUrl;
  /**
   * In-memory cache: `GET .../pull-testing/pilings/by-location` per
   * `(projectID, locationKey)` for the app session.
   */
  private readonly pilingsByLocationCache = new Map<string, PullTestPiling[]>();

  /** Install page listens to preload a piling chosen from “today’s pull test pilings” sidenav. */
  private readonly installPreloadPullTestPilingId = new Subject<string>();
  readonly installPreloadPullTestPilingId$ = this.installPreloadPullTestPilingId.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /** Emitted when the operator picks a row in the pull-test “today’s pilings” sidenav. */
  emitInstallPreloadPullTestPiling(pullTestPilingID: string): void {
    const t = pullTestPilingID?.trim();
    if (t) {
      this.installPreloadPullTestPilingId.next(t);
    }
  }

  /**
   * Pull test pilings this user installed today in the project (`GET …/reports/user/{userID}/pull-testing/today`).
   */
  getUserPullTestPilingUpdatesToday(
    projectID: string,
    userID: string,
    startKey?: string
  ): Observable<ApiPaginatedResponse<PullTestPilingUpdate[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/reports/user/${encodeURIComponent(userID)}/pull-testing/today`;
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        let params = new HttpParams()
          .set('projectID', projectID)
          .set('limit', '50')
          .set('startDate', startOfDay.toISOString())
          .set('endDate', endOfDay.toISOString());
        if (startKey) {
          params = params.set('startKey', startKey);
        }
        return this.http.get<ApiPaginatedResponse<PullTestPilingUpdate[]>>(url, { headers, params });
      })
    );
  }

  getModuleConfig(projectID: string): Observable<ApiResponse<PullTestingModule>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers =>
        this.http.get<ApiResponse<PullTestingModule>>(`${this.apiUrl}/projects/${projectID}/pull-testing`, { headers })
      )
    );
  }

  /**
   * Aggregated status counts for the pull testing dashboard.
   * Returns empty counts when the API responds 404 (no pull test pilings yet).
   */
  getModuleStatus(projectID: string): Observable<ApiResponse<PullTestingModuleStatus>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/status`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers =>
        this.http.get<ApiResponse<PullTestingModuleStatus>>(url, { headers }).pipe(
          catchError(err => {
            if (err?.status === 404) {
              return of({
                data: emptyPullTestingModuleStatus(),
                message: 'No pull test pilings',
                code: ApiErrorCode.Success,
              });
            }
            return throwError(() => err);
          })
        )
      )
    );
  }

  listPullTestPilings(
    projectID: string,
    options?: { limit?: number; startKey?: string; status?: string }
  ): Observable<ApiPaginatedResponse<PullTestPiling[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (options?.limit) params = params.set('limit', String(options.limit));
        if (options?.startKey) params = params.set('startKey', options.startKey);
        if (options?.status) params = params.set('status', options.status);
        const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings`;
        return this.http.get<ApiPaginatedResponse<PullTestPiling[]>>(url, { headers, params });
      })
    );
  }

  private pilingsByLocationCacheKey(projectID: string, locationSerial: number, pilePrefix: string): string {
    return `${projectID}|${buildPullTestLocationKey(pilePrefix, locationSerial)}`;
  }

  /** Drop cached siblings after install/test mutates a location. */
  clearPullTestPilingsForLocationCache(
    projectID: string,
    locationSerial: number,
    pilePrefix: string
  ): void {
    this.pilingsByLocationCache.delete(this.pilingsByLocationCacheKey(projectID, locationSerial, pilePrefix));
  }

  /**
   * Full sibling pilings at one location for install/test identifier pickers.
   * `GET /projects/{projectID}/pull-testing/pilings/by-location?locationKey={prefix}-{serial}`.
   */
  loadPullTestPilingsForLocation(
    projectID: string,
    locationSerial: number,
    pilePrefix: string
  ): Promise<PullTestPiling[]> {
    const cacheKey = this.pilingsByLocationCacheKey(projectID, locationSerial, pilePrefix);
    const hit = this.pilingsByLocationCache.get(cacheKey);
    if (hit) {
      return Promise.resolve(hit);
    }
    const locationKey = buildPullTestLocationKey(pilePrefix, locationSerial);
    return this.loadAllPullTestPilingsAtLocationKey(projectID, locationKey).then(rows => {
      this.pilingsByLocationCache.set(cacheKey, rows);
      return rows;
    });
  }

  /**
   * Distinct `pileIdentifier` values at one location (derived from {@link loadPullTestPilingsForLocation}).
   */
  loadPileIdentifiersForLocation(
    projectID: string,
    locationSerial: number,
    pilePrefix: string
  ): Promise<string[]> {
    return this.loadPullTestPilingsForLocation(projectID, locationSerial, pilePrefix).then(rows =>
      this.normalizePileIdentifierList(rows.map(r => r.pileIdentifier))
    );
  }

  private normalizePileIdentifierList(v: (string | undefined | null)[] | undefined | null): string[] {
    if (!v?.length) {
      return [];
    }
    return [...new Set(v.map(s => String(s ?? '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  /** Sibling pilings at one location (`locationKey` = `{pilePrefix}-{locationSerial}`). */
  listPullTestPilingsByLocation(
    projectID: string,
    locationKey: string,
    options?: { limit?: number; startKey?: string }
  ): Observable<ApiPaginatedResponse<PullTestPiling[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams().set('locationKey', locationKey);
        if (options?.limit) params = params.set('limit', String(options.limit));
        if (options?.startKey) params = params.set('startKey', options.startKey);
        const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings/by-location`;
        return this.http.get<ApiPaginatedResponse<PullTestPiling[]>>(url, { headers, params });
      })
    );
  }

  /**
   * Pile identifiers at `locationSerial` whose piling has coarse status `completed`.
   * Used to disable identifier chips on install / test lookup.
   */
  loadCompletedPileIdentifiersAtLocation(
    projectID: string,
    locationSerial: number,
    pilePrefix: string
  ): Promise<Set<string>> {
    return this.loadPullTestPilingsForLocation(projectID, locationSerial, pilePrefix).then(rows =>
      collectCompletedPileIdentifiers(rows)
    );
  }

  private async loadAllPullTestPilingsAtLocationKey(
    projectID: string,
    locationKey: string
  ): Promise<PullTestPiling[]> {
    const all: PullTestPiling[] = [];
    let startKey: string | undefined;
    for (let page = 0; page < PILE_IDENTIFIERS_BY_LOCATION_MAX_PAGES; page++) {
      const res = await firstValueFrom(
        this.listPullTestPilingsByLocation(projectID, locationKey, {
          limit: PILE_IDENTIFIERS_BY_LOCATION_PAGE_SIZE,
          startKey,
        })
      );
      const rows = res?.data ?? [];
      if (rows.length) {
        all.push(...rows);
      }
      const nk = res?.nextKey;
      if (!nk || String(nk).length === 0) {
        break;
      }
      startKey = String(nk);
    }
    return all;
  }

  getPullTestPiling(projectID: string, pullTestPilingID: string): Observable<ApiResponse<PullTestPiling>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings/${encodeURIComponent(pullTestPilingID)}`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => this.http.get<ApiResponse<PullTestPiling>>(url, { headers }))
    );
  }

  createPullTestPiling(
    projectID: string,
    body: Record<string, unknown>
  ): Observable<ApiResponse<PullTestPiling>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => this.http.post<ApiResponse<PullTestPiling>>(url, body, { headers }))
    );
  }

  /**
   * Preferred transactional create after wizard review (max 99 pilings per request).
   */
  bulkCreatePullTestPilings(
    projectID: string,
    body: BulkCreatePullTestPilingsRequest
  ): Observable<ApiResponse<unknown>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings/bulk`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => this.http.post<ApiResponse<unknown>>(url, body, { headers }))
    );
  }

  /**
   * Partial update for installation, soak, coarse status, and `*TestResults` step merges.
   * Pass `clientMutationId` when replaying offline queue items (`X-Client-Mutation-Id` header).
   */
  patchPullTestPiling(
    projectID: string,
    pullTestPilingID: string,
    body: Record<string, unknown>,
    options?: { clientMutationId?: string }
  ): Observable<ApiResponse<PullTestPiling>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings/${encodeURIComponent(pullTestPilingID)}`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let h = headers;
        const mutationId = options?.clientMutationId?.trim();
        if (mutationId) {
          h = headers.set('X-Client-Mutation-Id', mutationId);
        }
        return this.http.patch<ApiResponse<PullTestPiling>>(url, body, { headers: h });
      })
    );
  }

  /**
   * @deprecated Prefer `patchPullTestPiling`. `PUT` is accepted by the API as an alias of PATCH.
   */
  updatePullTestPiling(
    projectID: string,
    pullTestPilingID: string,
    body: Record<string, unknown>
  ): Observable<ApiResponse<PullTestPiling>> {
    return this.patchPullTestPiling(projectID, pullTestPilingID, body);
  }

  soakBypass(
    projectID: string,
    pullTestPilingID: string,
    reason: string,
    options?: { clientMutationId?: string }
  ): Observable<ApiResponse<PullTestPiling>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings/${encodeURIComponent(
      pullTestPilingID
    )}/soak/bypass`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let h = headers;
        const mutationId = options?.clientMutationId?.trim();
        if (mutationId) {
          h = headers.set('X-Client-Mutation-Id', mutationId);
        }
        return this.http.post<ApiResponse<PullTestPiling>>(
          url,
          { reason, confirmed: true },
          { headers: h }
        );
      })
    );
  }

  deletePullTestPiling(projectID: string, pullTestPilingID: string): Observable<ApiResponse<void>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/pilings/${encodeURIComponent(pullTestPilingID)}`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => this.http.delete<ApiResponse<void>>(url, { headers }))
    );
  }

  /**
   * Start async generation of the Pile Load Test Log (`POST …/pull-testing/reports/load-test-log`).
   * Recipients receive email with a token download link when the `.xlsx` is ready.
   */
  requestPileLoadTestLogReport(
    projectID: string,
    body: PileLoadTestLogReportRequest
  ): Observable<ApiResponse<unknown>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/reports/load-test-log`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => this.http.post<ApiResponse<unknown>>(url, body, { headers }))
    );
  }

  /**
   * Start async generation of the Pile Summary (`POST …/pull-testing/reports/pile-summary`).
   * Recipients receive email with a token download link when the `.xlsx` is ready.
   */
  requestPileSummaryReport(
    projectID: string,
    body: PileSummaryReportRequest
  ): Observable<ApiResponse<unknown>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/reports/pile-summary`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => this.http.post<ApiResponse<unknown>>(url, body, { headers }))
    );
  }

  /**
   * Append a Pull Testing audit row to **`project-actions`** (same table as Piled).
   * POST body: **`moduleType`** (required, `pullTesting`), **`actionType`**, **`pullTestPilingID`**, **`context`**, **`details`**.
   */
  recordPullTestingProjectAction(
    projectID: string,
    body: PullTestingProjectActionPayload | Record<string, unknown>,
    options?: { clientMutationId?: string }
  ): Observable<ApiResponse<unknown>> {
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/project-actions`;
    const mutationId = options?.clientMutationId?.trim();
    const payload = {
      ...body,
      moduleType: PULL_TESTING_PROJECT_ACTION_MODULE_TYPE,
      ...(mutationId ? { offlineSync: { clientMutationId: mutationId } } : {}),
    };
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let h = headers;
        if (mutationId) {
          h = headers.set('X-Client-Mutation-Id', mutationId);
        }
        return this.http.post<ApiResponse<unknown>>(url, payload, { headers: h });
      })
    );
  }

  /**
   * Pull Testing rows from **`project-actions`** (`GET …/pull-testing/events-log`).
   * Default feed: validation audits, `pull_test_piling_installed`, and modality completions.
   */
  listPullTestingEventsLog(
    projectID: string,
    options?: PullTestingEventsLogQuery
  ): Observable<ApiPaginatedResponse<PullTestEventsLogEntry[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/pull-testing/events-log`;
        let params = new HttpParams();
        if (options?.startDate) params = params.set('startDate', options.startDate);
        if (options?.endDate) params = params.set('endDate', options.endDate);
        if (options?.pullTestPilingID) {
          params = params.set('pullTestPilingID', options.pullTestPilingID);
        }
        if (options?.userName) params = params.set('userName', options.userName);
        if (options?.actionType) params = params.set('actionType', options.actionType);
        if (options?.eventCategory) params = params.set('eventCategory', options.eventCategory);
        if (options?.collisionStatus) {
          params = params.set('collisionStatus', options.collisionStatus);
        }
        if (options?.limit != null) params = params.set('limit', String(options.limit));
        if (options?.startKey) params = params.set('startKey', options.startKey);
        return this.http.get<ApiPaginatedResponse<PullTestEventsLogEntry[]>>(url, { headers, params });
      })
    );
  }

  /**
   * PM resolve a pending offline sync collision
   * (`POST …/pull-testing/collisions/{collisionId}/resolve`).
   */
  resolveCollision(
    projectID: string,
    collisionId: string,
    body: ResolveCollisionRequest
  ): Observable<ApiResponse<ResolveCollisionResponseData>> {
    const encodedId = encodeURIComponent(collisionId);
    const url = `${this.apiUrl}/projects/${projectID}/pull-testing/collisions/${encodedId}/resolve`;
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<ResolveCollisionResponseData>>(url, body, { headers })
      )
    );
  }
}
